import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  Optional,
  ViewChild,
  OnDestroy,
  NgZone,
} from '@angular/core';

import { translate, translateObject } from '@jsverse/transloco';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { DropdownObject, SearchBox_onChangeOverrideEvent, TabObject } from '@inmoment/the-kitchen';

import { Constants } from 'app/constants/constants';
import {
  AnalyticItemFilterSets,
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  Filter,
  FilterConfigChoice,
  FilterService,
  FilterSet,
  getSectionFilters,
  NumericFilterDisplayValue,
  OperatorType,
} from 'app/modules/collection/services/filter.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  WidgetGroup,
  Widget,
  Dashboard,
  Comparison,
} from 'app/providers/bridge-api/dashboard.service';
import { SectionMetadata } from 'app/providers/storage-api/collection.service';
import {
  DocumentService,
  FacetList,
  FacetType,
  SearchOptions,
} from 'app/providers/storage-api/document.service';
import { CustomField, FieldService } from 'app/providers/storage-api/field.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService, MAT_DIALOG_DATA, MatDialogRef } from 'app/services/dialog.service';
import { LocalStorageKey, LocalStorageService } from 'app/services/local-storage.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SectionService } from 'app/services/section.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetDataSource } from '../../../../models';
import { LexDatePipe } from '../../../../pipes/lex-date.pipe';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import { SelectedSection } from '../section-selector/section-selector.component';

export interface WidgetFilterDialogOptions {
  widget: Widget;
  projectId: string;
  analyticItem: string;
  sentimentScale: number;
  filters: AnalyticItemFilterSets;
  widgetDataSource: WidgetDataSource;
  widgetGroup: WidgetGroup;
  forWidgetGroup: boolean;
  userSearch?: any;
  dashboard?: Dashboard;
  comparison?: Comparison;
}

interface AnalyticItemKeyItem {
  subLabels: {
    labels: string[];
    displayLabels: string[];
    keys?: { [key: string]: any[] };
  };
  keys: string[];
  isMultiLevel: boolean;
  item: string;
  choicesFor?: FilterConfigChoice[] | DropdownObject[];
  hasChoicesFor?: boolean;
  initialized?: boolean;
  hasResults?: boolean;
}

interface AnalyticItemTreeModel {
  [key: string]: AnalyticItemKeyItem;
}

const TEXTUAL_SIZE = 'textualSize';

export function isAnalyticItemTextualSize(analyticItem: string): boolean {
  return analyticItem === TEXTUAL_SIZE;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-filter',
  templateUrl: './widget-filter.component.html',
  styleUrls: ['./widget-filter.component.scss'],
  standalone: false,
})
export class WidgetFilterDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('accordion') accordion: ElementRef;

  private facets: any = {};

  private originalFilterSet: AnalyticItemFilterSets;
  private originalUserSearches: FilterSet = { and: [], or: [], not: [] };
  private originalSelectedFilters: FilterSet;

  private filters: AnalyticItemFilterSets;
  private displayedUserSearchFilters: FilterSet = { and: [], or: [], not: [] };
  public userSavedSearches: string[] = [];
  private localStorageSavedSearchKey: string;
  private sessionStorageSavedSearchKey: string;
  private localStorageSearches: string[];
  private sessionStorageSearches: string[];

  private itemPages: any = {};

  private customFields: CustomField[] = [];
  private filterableCustomFields: CustomField[] = []; //List of the fields that have filterable set a true
  private filteredFilterableCustomFields: CustomField[] = []; // List of the above so that the popover search works
  private temporarilyFilterableCustomFields: string[] = []; // List of fields that are temporarily available to filter by.
  private nonFilterableCustomFields: CustomField[] = []; // List of fields that have filterable set as false
  private filteredUnfilterableCustomFields: CustomField[] = []; // List of the above so that the popover search works
  private popOverSelectedMetadata: CustomField[] = []; // Selected fields of the popover list
  addfieldPopoverMode = false;
  private activePopover: NgbPopover;
  private metaTemporaryChecked = false;

  query = '';
  modelDaterangeFrom: Date;
  modelDaterangeTo: Date;
  modelNumericValue: NumericFilterDisplayValue = {
    leftOperand: {
      operator: undefined,
      value: undefined,
    },
    rightOperand: {
      operator: undefined,
      value: undefined,
    },
  };
  modelSearch = '';
  modelEqualsExistsRadio: '_equals_' | '_exists_' | '_notExists_' = '_equals_';

  isLoading = { filters: true };
  selected: Filter[] = [];
  displayedSelectedFilters: FilterSet = { and: [], or: [], not: [] };
  originalTaxonomySelectedItems: string[] = [];
  currentAccordionItem: {
    item: string;
    rollupLabel: string;
  } = {
    item: undefined,
    rollupLabel: undefined,
  };

  filterChangeCount = 0;

  analyticItems: string[] = [];
  analyticItemKeys: AnalyticItemTreeModel;
  noAnalyticMatch: boolean;

  metadataItems: string[] = [];
  metadataItemKeys: AnalyticItemTreeModel;
  noMetadataMatch: boolean;
  noMetadataText: string;

  userQueryInvalidText;

  selectedMap = {};
  headerText = '';
  analyticItemDisplayMap = {};
  analyticItemKeyDisplayMap = {};
  analyticItemKeyCountMap = {};
  isOperatorAllowedMap = {};
  configTypeMap = {};
  selectedDateChoice;
  dateResolution: 'relative' | 'fixed';
  widgetDataSource: WidgetDataSource;
  public widgetFilterTabs: TabObject[] = [
    { title: translate('widgets.widget-filter.analytics'), id: 'analytics', disabled: false },
    { title: translate('widgets.widget-filter.metadata'), id: 'metadata', disabled: false },
    { title: translate('widgets.widget-filter.taxonomy'), id: 'taxonomy', disabled: false },
  ];
  public activeTab = 'analytics';

  private unsub$ = new Subject<void>();

  isSectionFilteringActive = false;
  sectionMetadata: SectionMetadata[] = [];
  isSectionFilteringInitialized = false;
  sectionFieldDs: DropdownObject[];
  sectionBucketDs: DropdownObject[];
  selectedSections: SelectedSection[];
  sectionSelectionText: string;
  sectionFilters: Filter[] = [];
  canAddToSection = false;
  userId: string;
  filterSelectedSections: SelectedSection[];
  hasWgSectionFilter = false;
  readonly ALL_SECTION_ID = Constants.ALL_SECTION_ID;

  private readonly standardFields = FilterService.standardFields.filter((f) => f !== 'title');
  sectionFilterTabs: TabObject[] = [
    {
      title: translate('widgets.widget-filter.document-filters'),
      id: 'documents',
      disabled: false,
    },
  ];
  activeSectionTab = 'documents';

  constructor(
    private host: ElementRef,
    private filterService: FilterService,
    private documentService: DocumentService,
    private alertService: AlertService,
    private dialogRef: MatDialogRef<WidgetFilterDialogComponent>,
    private dialogService: DialogService,
    private timezoneService: TimezoneService,
    private accountService: StorageAccountService,
    private lexDatePipe: LexDatePipe,
    private fieldService: FieldService,
    private ngZone: NgZone,
    private sectionService: SectionService,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private logger: LoggerService,
    @Optional() @Inject(MAT_DIALOG_DATA) private options: WidgetFilterDialogOptions
  ) {
    this.dialogRef.disableClose = true;
    if (!this.options.filters) {
      this.options.filters = {
        topics: { and: [], or: [], not: [] },
        entities: { and: [], or: [], not: [] },
        themes: { and: [], or: [], not: [] },
        phrases: { and: [], or: [], not: [] },
        dates: { and: [], or: [], not: [] },
        analyses: { and: [], or: [], not: [] },
        metadata: { and: [], or: [], not: [] },
        tags: { and: [], or: [], not: [] },
        core: { and: [], or: [], not: [] },
        taxonomy: { and: [], or: [], not: [] },
        sectionProperties: { and: [], or: [], not: [] },
      };
    }
    if (!this.options.userSearch) {
      this.options.userSearch = { and: [], or: [], not: [] };
    }
    this.filters = JSON.parse(JSON.stringify(this.options.filters));
    this.displayedUserSearchFilters = JSON.parse(JSON.stringify(this.options.userSearch)) || {
      and: [],
      or: [],
      not: [],
    };
  }

  async ngOnInit() {
    this.userId = this.authService.accountId;
    this.widgetDataSource = this.options.widgetDataSource;
    this.accountService.groupFieldsSub.takeUntil(this.unsub$).subscribe((accountMetadata) => {
      this.customFields = accountMetadata.metadata.map((f) => ({
        id: f.id,
        label: f.label,
        description: f.description,
        fieldName: `metadata.${f.fieldName}`,
        fieldType: f.fieldType,
        isFilterable: f.metadata?.filterable ?? true,
        aliasedFields: f.aliasedFields,
        isDisplayed: f.metadata?.display ?? true,
        calculationType: f.metadata?.calculationType,
        calculationDefinition: f.metadata?.calculationDefinition,
        calculationFieldType: f.metadata?.calculationFieldType,
      }));
      if (!this.options.forWidgetGroup) {
        this.widgetFilterTabs.push({
          title: translate('widgets.widget-filter.text'),
          id: 'text',
          disabled: false,
        });
      }
      if (this.options.widgetDataSource.isSocial) {
        this.initializeSocialCustomFields();
      }
      this.displayedSelectedFilters = this.initializeDisplayedFilters();
      this.originalSelectedFilters = JSON.parse(JSON.stringify(this.displayedSelectedFilters));
      this.query = this.initializeQuery();
      this.setHeaderText();
      this.resetFilters();
      this.refresh();
    });

    if (!this.options.forWidgetGroup) {
      this.getPrevUserSearches();
    }

    if (this.showSections) {
      await this.initSectionFiltering();
    }
  }

  ngOnDestroy(): void {
    this.closePopover(true);
    this.unsub$.next();
    this.unsub$.complete();
  }

  public get hasChanges() {
    return !!this.filterChangeCount;
  }

  public get widget(): Widget {
    return this.options.widget;
  }

  private initializeQuery(): string {
    const tempWidget = JSON.parse(JSON.stringify(this.widget));
    tempWidget.payload.filters = this.filters;
    const filterOptions = this.filterService.getWidgetQueryOptions(
      tempWidget,
      this.options.widgetDataSource,
      this.options.widgetGroup,
      this.options.comparison?.filters[0]
    );
    return this.filterService.getQueryForDocuments(filterOptions, false, true, true);
  }

  private initializeDisplayedFilters() {
    const displayedFilters = { and: [], or: [], not: [] };

    if (this.filters) {
      for (const key of Object.keys(this.filters)) {
        for (const operator of ['and', 'or', 'not']) {
          if (this.filters[key][operator]) {
            for (const filter of this.filters[key][operator]) {
              if (filter.hidden) {
                continue;
              }
              filter.displayTitle = this.translateTitleFor(
                filter.analyticItem,
                operator,
                filter.displayName || filter.value
              );
              filter.displayValue = this.translateKeyFor(
                filter.analyticItem,
                filter.displayName || filter.value
              );
              displayedFilters[operator].push(filter);
            }
          }
        }
      }
    }

    //Add active userSearches from payload to displayedFilters
    if (this.displayedUserSearchFilters) {
      for (const operator of ['and', 'or', 'not']) {
        if (this.displayedUserSearchFilters[operator]) {
          for (const query of this.displayedUserSearchFilters[operator]) {
            query.displayTitle = this.translateTitleFor(
              query.analyticItem,
              operator,
              query.displayName || query.value
            );
            query.displayValue = this.translateKeyFor(
              query.analyticItem,
              query.displayName || query.value
            );
            displayedFilters[operator].push(query);
          }
        }
      }
    }

    if (this.options.widgetGroup && !this.forWidgetGroup()) {
      const filterGroupFilters = JSON.parse(JSON.stringify(this.options.widgetGroup.filters));
      for (const key of Object.keys(filterGroupFilters)) {
        for (const operator of ['and', 'or', 'not']) {
          if (filterGroupFilters[key][operator]) {
            for (const filter of filterGroupFilters[key][operator]) {
              if (filter.hidden) {
                continue;
              }
              filter['displayTitle'] = this.translateTitleFor(
                filter['analyticItem'],
                operator,
                filter['displayName'] || filter['value']
              );
              filter['displayValue'] = this.translateKeyFor(
                filter['analyticItem'],
                filter['displayName'] || filter['value']
              );
              filter['fromWidgetGroup'] = true;
              displayedFilters[operator].push(filter);
            }
          }
        }
      }
    }

    if (
      this.options.widgetGroup &&
      this.options.widgetGroup.parentWidget &&
      this.options.widgetGroup.parentWidget !== this.widget.id
    ) {
      const filterGroupParentFilters = JSON.parse(
        JSON.stringify(this.options.widgetGroup.parentFilters)
      );
      for (const key of Object.keys(filterGroupParentFilters)) {
        for (const operator of ['and', 'or', 'not']) {
          if (filterGroupParentFilters[key][operator]) {
            for (const filter of filterGroupParentFilters[key][operator]) {
              if (filter.hidden) {
                continue;
              }
              filter['displayTitle'] = this.translateTitleFor(
                filter['analyticItem'],
                operator,
                filter['displayName'] || filter['value']
              );
              filter['displayValue'] = this.translateKeyFor(
                filter['analyticItem'],
                filter['displayName'] || filter['value']
              );
              filter['fromWidgetGroupParent'] = true;
              displayedFilters[operator].push(filter);
            }
          }
        }
      }
    }

    return displayedFilters;
  }

  private initializeFacets() {
    const initialFacets: any = FilterService.analysisFields.reduce((prev, curr) => {
      let facetKey = curr;
      if (facetKey.endsWith('sentimentPolarity') && this.options.sentimentScale) {
        facetKey = facetKey + this.options.sentimentScale;
      }

      if (
        facetKey.includes('analyses.sentimentPolarity') ||
        (facetKey.includes('entities') && this.widgetDataSource.hasEntities) ||
        (facetKey.includes('phrases') && this.widgetDataSource.hasPhrases) ||
        (facetKey.includes('themes') && this.widgetDataSource.hasThemes) ||
        (facetKey.includes('topics') && this.widgetDataSource.hasTopics) ||
        (facetKey.includes('eie') && this.widgetDataSource.clauseModelTypes?.length) ||
        (facetKey.includes('sc_') && this.widgetDataSource.isSocial)
      ) {
        prev[facetKey] = {};
      }

      return prev;
    }, {});

    if (this.widgetDataSource.hasDateData) {
      initialFacets.createdAt = {};
    }

    for (const stdField of this.widgetDataSource.standardFields) {
      initialFacets[stdField] = {};
    }

    if (this.widgetDataSource.isSocial) {
      initialFacets['metadata.sc_Place'] = {};
      initialFacets['metadata.sc_sourceType'] = {};
      initialFacets['metadata.sc_domain'] = {};
      initialFacets['metadata.sc_author'] = {};
    }

    //If Field is alias, and contains datasource custom fields, use it instead of original fields
    dataSourceFields: for (const metadataField of this.widgetDataSource.customFields) {
      for (const field of this.customFields) {
        if (field.aliasedFields?.includes(metadataField)) {
          initialFacets[field.fieldName] = {};
          continue dataSourceFields;
        }
      }
      initialFacets[`metadata.${metadataField}`] = {};
    }

    this.widgetDataSource.calculatedFields?.forEach((f) => (initialFacets[`metadata.${f}`] = {}));

    return initialFacets;
  }

  onTabChange() {
    this.selected = [];
    this.setIsOperatorAllowed();
  }

  public keysFor(analyticItem: string, subLabel?: string): string[] {
    const choices = this.choicesFor(analyticItem, subLabel);
    if (choices && choices.length) {
      return choices.map((x) => x.id ?? x.key ?? x.value?.toString() ?? null);
    }

    if (!this.facets[analyticItem]) {
      return [];
    }

    let bucket;
    if (this.isMultiLevel(analyticItem)) {
      bucket = this.facets[analyticItem][subLabel]['buckets'];
    } else {
      bucket = this.facets[analyticItem];
    }
    const keys = Object.getOwnPropertyNames(bucket).filter(
      (x) => !this.isAdded(analyticItem, x, subLabel)
    );

    let page = this.getCurrentItemPage(analyticItem, subLabel);

    if (!page) {
      page = {
        size: 10,
        current: 1,
        length: keys.length,
      };

      if (subLabel) {
        this.itemPages[analyticItem] = this.itemPages[analyticItem] || {};
        this.itemPages[analyticItem][subLabel] = page;
      } else {
        this.itemPages[analyticItem] = page;
      }
    }

    return keys.sort((a, b) => bucket[b] - bucket[a]).slice(0, page.current * page.size);
  }

  showEqualsExistsRadios(analyticItem: string): boolean {
    return this.activeTab === 'metadata' && !this.isStandardField(analyticItem);
  }

  setExpandedState(state: boolean, analyticItem: string, rollupLabel?: string) {
    if (state) {
      this.currentAccordionItem = {
        item: analyticItem,
        rollupLabel: this.currentAccordionItem.rollupLabel || rollupLabel,
      };

      let analyticItemModel = this.analyticItemKeys[analyticItem];
      const itemIsInMetadataKeys =
        this.isMetadataAnalyticItem(analyticItem) ||
        this.isCreatedAtAnalyticItem(analyticItem) ||
        this.isStandardField(analyticItem) ||
        this.isTextualSize(analyticItem);
      if (itemIsInMetadataKeys) {
        analyticItemModel = this.metadataItemKeys[analyticItem];
      }

      if (analyticItemModel && !analyticItemModel.initialized) {
        const options: SearchOptions = {};
        if (itemIsInMetadataKeys) {
          options.facetMetadataName = [this.metadataItemKeys[analyticItem].item];
        }
        this.search(this.query, [this.getFacetTypeByAnalyticItem(analyticItem)], options)
          .then((facets) => {
            this.facets = Object.assign(this.facets, facets);
            this.initializeAnalyticsTab();
            this.initializeMetadataTab();
          })
          .catch((err) => {
            switch (err.error?.type) {
              case 'QuerySyntaxInvalid':
              case 'ElasticRestStatusException':
                this.userQueryInvalidText = translate(
                  'widgets.widget-filter.no-results-for-filters-invalid-query'
                );

                if (this.widget.payload.userQuery) {
                  this.logger.error('Invalid user query');
                }
                break;
              case 'InternalServerException':
                this.userQueryInvalidText = translate(
                  'widgets.widget-filter.no-results-for-filters-query-error'
                );
                this.alertService.showErrorToast(`${this.userQueryInvalidText}: ${err.error.data}`);
                break;
            }
          })
          .finally(() => {
            if (itemIsInMetadataKeys) {
              this.metadataItemKeys[analyticItem].initialized = true;
            } else {
              this.analyticItemKeys[analyticItem].initialized = true;
            }
          });
      }
    } else {
      if (analyticItem === this.currentAccordionItem.item) {
        this.currentAccordionItem = {
          item: rollupLabel ? this.currentAccordionItem.item : undefined,
          rollupLabel: undefined,
        };
      }

      if (analyticItem.includes('createdAt')) {
        this.selected = this.selected.filter((filter) => filter.analyticItem !== analyticItem);
        this.modelDaterangeFrom = undefined;
        this.modelDaterangeTo = undefined;
      } else if (this.isNumericCustomField(analyticItem) || this.isTextualSize(analyticItem)) {
        this.selected = this.selected.filter((filter) => filter.analyticItem !== analyticItem);
        this.resetNumericRange();
      }

      this.modelEqualsExistsRadio = '_equals_';

      this.setIsOperatorAllowed();
    }
  }

  private resetNumericRange() {
    this.modelNumericValue = {
      leftOperand: {
        operator: undefined,
        value: undefined,
      },
      booleanOperator: undefined,
      rightOperand: {
        operator: undefined,
        value: undefined,
      },
    };
  }

  public showMore(analyticItemKeys, analyticItem, rollupLabel?) {
    const page = this.getCurrentItemPage(analyticItem, rollupLabel);
    page.current += 1;

    if (rollupLabel) {
      const keys = this.keysFor(analyticItem, rollupLabel);
      analyticItemKeys[analyticItem].subLabels.keys[rollupLabel] = keys;
      for (const key of keys) {
        this.analyticItemKeyDisplayMap[analyticItem + key] = this.translateKeyFor(
          analyticItem,
          key
        );
        this.analyticItemKeyCountMap[analyticItem + key + rollupLabel] = this.countFor(
          analyticItem,
          key,
          rollupLabel
        );
      }
    } else {
      const keys = this.keysFor(analyticItem);
      analyticItemKeys[analyticItem].keys = keys;
      for (const key of keys) {
        this.analyticItemKeyDisplayMap[analyticItem + key] = this.translateKeyFor(
          analyticItem,
          key
        );
        this.analyticItemKeyCountMap[analyticItem + key] = this.countFor(
          analyticItem,
          key,
          undefined
        );
      }
    }
  }

  hasMore(analyticItem, rollupLabel?) {
    const page = this.getCurrentItemPage(analyticItem, rollupLabel);

    return page.size * page.current < page.length;
  }

  private getCurrentItemPage(analyticItem, rollupLabel) {
    let page: any = this.itemPages[analyticItem];
    if (page && rollupLabel) {
      page = this.itemPages[analyticItem][rollupLabel];
    }

    return page;
  }

  public countFor(analyticItem: string, key: string, subLabel: string): number {
    if (this.isMultiLevel(analyticItem)) {
      if (this.facets[analyticItem] && this.facets[analyticItem][subLabel]) {
        return this.facets[analyticItem][subLabel].buckets
          ? (this.facets[analyticItem][subLabel].buckets[key] ??
              this.facets[analyticItem][subLabel].buckets[key])
          : 0;
      }
    }
    return this.facets[analyticItem]
      ? (this.facets[analyticItem][key] ?? this.facets[analyticItem][key])
      : 0;
  }

  // isMultiLevel looks like:
  //
  // JSON.stringify(temp1['ML-analyses.entities.title']['Job Title'], null, 4)
  // {
  //     "buckets": {
  //         "manager": 7,
  //         "chair": 7,
  //         "Director": 1
  //     },
  //     "count": 15
  // }

  public isMultiLevel(analyticItem: string): boolean {
    const filterConfig = this.filterService.getFilterConfigItem(analyticItem);
    if (!filterConfig) {
      return false;
    }
    return filterConfig.isMultiLevel;
  }

  public translateTitleFor(analyticItem: string, filterGroup?: string, value?: string): string {
    if (analyticItem.startsWith('metadata.')) {
      const name = analyticItem.replace(/^metadata\./, '');
      for (const field of this.customFields) {
        if (field.fieldName === analyticItem) {
          return field.description;
        }
      }
      return name;
    }

    if (
      filterGroup === 'and' &&
      this.isCreatedAtAnalyticItem(analyticItem) &&
      this.widget.payload.zoomDateFilter &&
      this.widget.payload.zoomDateFilter === value
    ) {
      return translate('widgets.widget-filter.zoomed-to');
    }

    if (this.isTextualSize(analyticItem)) {
      return translate('widgets.widget-filter.text-length');
    }

    return this.filterService.translateTitle(analyticItem);
  }

  private translateKeyFor(analyticItem: string, key: string): string {
    if (!analyticItem) {
      return undefined;
    }
    if (key === '') {
      return Constants.BLANK;
    }
    if (this.isDateCustomField(analyticItem) && !this.isExistsValue(key)) {
      return this.lexDatePipe.transform(key);
    }
    return this.filterService.translateKey(analyticItem, key);
  }

  public setConfigTypeFor(analyticItem: string) {
    if (this.isNumericCustomField(analyticItem)) {
      this.configTypeMap[analyticItem] = 'numeric';
      return;
    }

    if (this.isTextualSize(analyticItem)) {
      this.configTypeMap[analyticItem] = 'numeric';
      return;
    }

    if (this.isDateCustomField(analyticItem)) {
      this.configTypeMap[analyticItem] = 'daterange';
      return;
    }

    if (this.isMetadataAnalyticItem(analyticItem)) {
      this.configTypeMap[analyticItem] = 'checkbox';
      return;
    }

    const config = this.filterService.getFilterConfigItem(analyticItem);
    this.configTypeMap[analyticItem] = config ? config.type : 'checkbox';
  }

  public choicesFor(
    analyticItem: string,
    subLabel?: string
  ): FilterConfigChoice[] | DropdownObject[] {
    if (this.isNumericCustomField(analyticItem)) {
      return [];
    }

    if (this.isTextualSize(analyticItem)) {
      return [];
    }

    const config = this.filterService.getFilterConfigItem(analyticItem);
    if (!config || !config.choices) {
      return;
    }

    if (
      (config.type === 'radio' || config.type === 'date') &&
      config.choices.some((c) => this.isAdded(analyticItem, c.key, subLabel))
    ) {
      return;
    }

    if (config.type === 'date') {
      return config.choices
        .filter((c) => c.picker !== 'daterange')
        .map((c) => ({ title: c.label, id: c.key }));
    }

    return config.choices.filter((x) => {
      return !this.isAdded(analyticItem, x.value as string, subLabel);
    });
  }

  private setSelectedMapValue(
    value: boolean,
    analyticItem: string,
    analyticItemValue: string,
    subLabel?: string
  ) {
    const mapKey = analyticItem + analyticItemValue + (subLabel ?? '');
    this.selectedMap[mapKey] = value;
  }

  public toggleSelected(analyticItem: string, value: string, subLabel?: string): void {
    const index = this.isSelected(analyticItem, value, subLabel);
    if (index >= 0) {
      this.selected.splice(index, 1);
      this.setSelectedMapValue(false, analyticItem, value, subLabel);
    } else {
      this.selected.push({ analyticItem, value, subLabel });
      this.setSelectedMapValue(true, analyticItem, value, subLabel);
    }
    this.setIsOperatorAllowed();
  }

  public removeIfSelected(analyticItem: string, value: string, subLabel?: string): void {
    const index = this.isSelected(analyticItem, value, subLabel);
    if (index >= 0) {
      this.selected.splice(index, 1);
      this.setSelectedMapValue(false, analyticItem, value, subLabel);
      this.setIsOperatorAllowed();
    }
  }

  public addIfNotSelected(analyticItem: string, value: string, subLabel?: string): void {
    const index = this.isSelected(analyticItem, value, subLabel);
    if (index === -1) {
      this.selected.push({ analyticItem, value, subLabel });
      this.setSelectedMapValue(true, analyticItem, value, subLabel);
      this.setIsOperatorAllowed();
    }
  }

  public isSelected(analyticItem: string, value: string, subLabel?: string): number {
    for (let i = 0; i < this.selected.length; ++i) {
      const selected = this.selected[i];
      if (
        selected.analyticItem === analyticItem &&
        selected.subLabel === subLabel &&
        (selected.key === value || selected.value === value)
      ) {
        return i;
      }
    }

    return -1;
  }

  public toggleSelectedChoice(
    analyticItem: string,
    choice: FilterConfigChoice,
    subLabel?: string
  ): void {
    const config = this.filterService.getFilterConfigItem(analyticItem);
    if (config.type === 'radio') {
      config.choices.forEach((c) =>
        this.removeIfSelected(analyticItem, c.key ? c.key : c.value.toString(), subLabel)
      );
    }

    return this.toggleSelected(
      analyticItem,
      choice.key ? choice.key : choice.value.toString(),
      subLabel
    );
  }

  toggleSelectedDateChoice(analyticItem: string, dateKey?: string) {
    if (this.dateResolution && dateKey) {
      this.dateResolution = 'relative';
    }
    this.selectedDateChoice = dateKey ?? this.selectedDateChoice;
    this.selected = this.selected.filter((f) => f.analyticItem !== analyticItem);

    let range;
    if (this.dateResolution === 'fixed') {
      range = this.getSelectedDaterange();
    } else {
      range = this.filterService.computeFixedValueForDateFilter(this.selectedDateChoice);

      this.modelDaterangeFrom = new Date(range[0]);
      this.modelDaterangeTo = new Date(range[1]);
    }

    if (this.dateResolution && (this.modelDaterangeFrom || this.modelDaterangeTo)) {
      this.addIfNotSelected(
        analyticItem,
        this.dateResolution === 'fixed' ? 'daterange' : this.selectedDateChoice
      );
    }
  }

  onDateChanged(
    analyticItem,
    dateRangeProp: 'modelDaterangeFrom' | 'modelDaterangeTo',
    date: Date
  ) {
    this[dateRangeProp] = date;
    this.dateResolution = 'fixed';

    this.toggleSelectedDateChoice(analyticItem);
  }

  public isSelectedChoice(
    analyticItem: string,
    choice: FilterConfigChoice,
    subLabel: string
  ): number {
    return this.isSelected(
      analyticItem,
      choice.key ? choice.key : choice.value.toString(),
      subLabel
    );
  }

  public toggleSelectedNumeric(analyticItem) {
    this.removeIfSelected(analyticItem, '_exists_');
    this.removeIfSelected(analyticItem, '_notExists_');
    this.removeIfSelected(analyticItem, 'numericrange');

    if (this.modelNumericValue.leftOperand.operator) {
      if (this.isExistsValue(this.modelNumericValue.leftOperand.operator)) {
        this.modelNumericValue.leftOperand.value = undefined;
        this.modelNumericValue.booleanOperator = undefined;
        this.modelNumericValue.rightOperand.operator = undefined;
        this.modelNumericValue.rightOperand.value = undefined;

        this.addIfNotSelected(analyticItem, this.modelNumericValue.leftOperand.operator);
      } else {
        if (this.modelNumericValue.leftOperand.value) {
          if (this.modelNumericValue.booleanOperator) {
            if (
              this.modelNumericValue.rightOperand.operator &&
              this.modelNumericValue.rightOperand.value
            ) {
              this.addIfNotSelected(analyticItem, 'numericrange');
            }
          } else {
            this.addIfNotSelected(analyticItem, 'numericrange');
          }
        }
      }
    }
  }

  public isModelNumericOperatorAllowed() {
    return (
      this.modelNumericValue.leftOperand.operator &&
      this.modelNumericValue.leftOperand.operator !== '=' &&
      !this.isExistsValue(this.modelNumericValue.leftOperand.operator) &&
      this.modelNumericValue.leftOperand.value
    );
  }

  public isNumericCustomField(analyticItem) {
    const field = this.customFields.find((cf) => cf.fieldName === analyticItem);
    return (
      field &&
      (field.fieldType === 'long' ||
        field.fieldType === 'double' ||
        field.calculationFieldType === 'long' ||
        field.calculationFieldType === 'double')
    );
  }

  public isDoubleCustomField(analyticItem) {
    const field = this.customFields.find((cf) => cf.fieldName === analyticItem);
    return field && field.fieldType === 'double';
  }

  public isDateCustomField(analyticItem) {
    const field = this.customFields.find((cf) => cf.fieldName === analyticItem);
    return field && field.fieldType === 'date';
  }

  isTextualSize(analyticItem: string): boolean {
    return isAnalyticItemTextualSize(analyticItem);
  }

  public getSelectedDaterange(): string[] {
    let strFrom = '*';
    let strTo = '*';

    if (this.modelDaterangeFrom) {
      strFrom = this.timezoneService
        .convertDate(this.modelDaterangeFrom)
        .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    }

    if (this.modelDaterangeTo) {
      // add 1 day to make it inclusive
      strTo = this.timezoneService
        .convertDate(this.modelDaterangeTo)
        .endOf('day')
        .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    }

    return [strFrom, strTo];
  }

  public setNumericOperator($event, analyticItem, operator) {
    this.modelNumericValue.booleanOperator = $event.checked ? operator : undefined;
    this.toggleSelectedNumeric(analyticItem);
  }

  public setIsOperatorAllowed() {
    const and: OperatorType = 'and';
    const or: OperatorType = 'or';
    const not: OperatorType = 'not';
    for (const operator of [and, or, not]) {
      for (const item of this.selected) {
        if (this.isNumericCustomField(item.analyticItem) || this.isTextualSize(item.analyticItem)) {
          this.isOperatorAllowedMap[operator] =
            this.modelNumericValue.leftOperand.operator &&
            this.modelNumericValue.leftOperand.value &&
            (!this.modelNumericValue.booleanOperator ||
              (this.modelNumericValue.rightOperand.operator &&
                this.modelNumericValue.rightOperand.value)) &&
            operator === 'and';
        }
      }
      this.isOperatorAllowedMap[operator] = true;
    }

    if (this.hasSections) {
      let canAddToSection = true;

      this.selected.forEach((f) => (canAddToSection &&= this.filterService.isAllowedForSection(f)));

      this.canAddToSection = canAddToSection;
    }
  }

  public isAdded(analyticItem: string, key: string, subLabel?: string): boolean {
    const operators = ['and', 'or', 'not'];

    for (const filterItem of Object.keys(this.filters)) {
      for (const operator of operators) {
        if (this.filters[filterItem][operator]) {
          for (const item of this.filters[filterItem][operator]) {
            if (
              item.analyticItem === analyticItem &&
              (item.key === key || item.value === key) &&
              (item.subLabel ? item.subLabel.split(',').includes(subLabel) : true)
            ) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  onTaxonomySelectionChanged($event) {
    const selectedTaxonomyItems = this.selected.filter((s) => s.analyticItem === 'taxonomy');
    $event.forEach((t) => {
      const idx = selectedTaxonomyItems.findIndex((st) => st.value === t.id);
      if (idx < 0) {
        this.selected.push({ analyticItem: 'taxonomy', value: t.id, displayName: t.name });
      }
    });

    selectedTaxonomyItems.forEach((st) => {
      const idx = $event.findIndex((t) => st.value === t.id);
      if (idx < 0) {
        this.selected.splice(idx, 1);
      }
    });
    this.setIsOperatorAllowed();
  }

  onSearchSelectionChanged(selected) {
    this.selected = selected;
  }

  private moveFilter(
    oldOperator: OperatorType,
    newOperator: OperatorType | 'document' | 'section',
    filter: Filter
  ) {
    if ((filter.sectionField || filter.selectedSections?.length) && oldOperator !== newOperator) {
      this.clearAndRemoveSectionFilter(filter);
    }

    if (newOperator === 'section') {
      this.setAndAddSectionFilter(filter);
    }

    // 'document' and 'section' are equal to 'and' operator
    if (oldOperator !== 'and' || (newOperator !== 'document' && newOperator !== 'section')) {
      if (newOperator === 'document' || newOperator === 'section') {
        newOperator = 'and';
      }

      const filterSet = this.getFilterSet(filter);
      let filters;
      if (filter.analyticItem !== 'userSearch') {
        filters = this.filters[filterSet][oldOperator];
      } else {
        filters = this.displayedUserSearchFilters[oldOperator];
      }

      const idx = filters.findIndex(
        (item) =>
          filter.analyticItem === item.analyticItem &&
          ((filter.key !== undefined && filter.key === item.key) || filter.value === item.value)
      );
      if (idx !== -1) {
        filters.splice(idx, 1);
      }

      if (filter.analyticItem !== 'userSearch') {
        this.filters[filterSet][newOperator].push(filter);
      } else {
        this.displayedUserSearchFilters[newOperator].push(filter);
      }
    }

    this.recalculateFilters(true);

    this.initializeAnalyticsTab();
    this.initializeMetadataTab();
  }

  private getFilterSet(filter: Filter): string {
    return this.filterService.getFilterSetNameByAnalyticItem(filter);
  }

  add(operator: OperatorType, forSection?: boolean): void {
    for (const selected of this.selected) {
      const filterSet = this.getFilterSet(selected);

      let filterToAdd = { ...selected };

      if (selected.analyticItem.includes('metadata') || this.isTextualSize(selected.analyticItem)) {
        if (
          this.isNumericCustomField(selected.analyticItem) ||
          this.isTextualSize(selected.analyticItem)
        ) {
          if (!this.isExistsValue(selected.value)) {
            filterToAdd = {
              analyticItem: selected.analyticItem,
              value: this.filterService.numericRangeModelToFilter(this.modelNumericValue),
            };
          }

          this.resetNumericRange();
        }
      } else if (selected.analyticItem.includes('tags')) {
      } else if (selected.analyticItem.includes('createdAt')) {
        let range;
        let value;
        let key;
        if (selected.value === 'daterange') {
          range = this.getSelectedDaterange();
          if (range[0] === '*' && range[1] === '*') {
            this.alertService.showErrorToast(translate('widgets.widget-filter.choose-from-or-to'));
            return;
          }
          value = `daterange/${range[0]}/${range[1]}`;
          key = `daterange`;
        } else {
          value = selected.value;
          key = selected.value;
        }

        filterToAdd = {
          analyticItem: selected.analyticItem,
          value,
          key,
        };

        if (!this.widgetDataSource.customFields?.length) {
          this.widgetFilterTabs.find((tab) => tab.id === 'metadata').disabledReason = translate(
            'widgets.widget-filter.no-filters-available'
          );
        }
      }

      if (forSection) {
        filterToAdd.selectedSections = this.selectedSections;

        this.sectionFilters.push(filterToAdd);
      }

      if (filterToAdd.analyticItem !== 'userSearch') {
        this.filters[filterSet][operator].push(filterToAdd);
      } else {
        this.displayedUserSearchFilters[operator].push(filterToAdd);
      }
    }

    this.recalculateFilters(true);

    this.initializeAnalyticsTab();
    this.initializeMetadataTab();

    this.selected = [];
  }

  onFilterAction(
    $event: {
      filterMoveOrDelete: OperatorType | 'remove' | 'document' | 'section';
      newFilter: Filter;
    },
    operator: OperatorType,
    filter: Filter
  ) {
    if ($event.filterMoveOrDelete) {
      const action = $event.filterMoveOrDelete;
      if (action === operator) {
        return;
      }
      switch (action) {
        case 'remove':
          this.remove(operator, filter);
          this.alertService.showToast(translate('widgets.widget-filter.one-filter-removed'));
          break;
        case 'and':
        case 'or':
        case 'not':
        case 'document':
        case 'section':
          this.moveFilter(operator, action, filter);
          break;
      }
    } else {
      this.moveFilter(operator, operator, filter);

      this.alertService.showToast(translate('widgets.widget-filter.one-filter-updated'));
    }
  }

  private remove(operator: OperatorType, filter: Filter): void {
    const filterSet = this.getFilterSet(filter);
    // first, remove the chip
    let filters: Filter[];
    if (filter.analyticItem !== 'userSearch') {
      filters = this.filters[filterSet][operator];
    } else {
      filters = this.displayedUserSearchFilters[operator];
    }

    const idx = filters.findIndex(
      (item) =>
        filter.analyticItem === item.analyticItem &&
        ((filter.key !== undefined && filter.key === item.key) || filter.value === item.value)
    );
    if (idx !== -1) {
      filters.splice(idx, 1);
    }

    const sectionIdx = this.sectionFilters.indexOf(filter);
    if (sectionIdx > -1) {
      this.sectionFilters.splice(sectionIdx, 1);
    }

    // then refresh if this filter was included when we last refreshed the data
    let filterWasIncluded: boolean;
    if (filter.analyticItem !== 'userSearch') {
      filterWasIncluded = this.originalFilterSet[filterSet][operator].some(
        (item) =>
          filter.analyticItem === item.analyticItem &&
          ((filter.key !== undefined && filter.key === item.key) || filter.value === item.value)
      );
    } else {
      filterWasIncluded = this.originalUserSearches[operator].some(
        (item) =>
          filter.analyticItem === item.analyticItem &&
          ((filter.key !== undefined && filter.key === item.key) || filter.value === item.value)
      );
    }
    this.recalculateFilters(!filterWasIncluded);

    if (filterWasIncluded) {
      this.resetFilters();
      this.refresh();
    } else {
      this.initializeAnalyticsTab();
      this.initializeMetadataTab();
    }
  }

  private recalculateFilters(countChange?) {
    const compareFilters = (source, target) => {
      let cnt = 0;

      Object.keys(source).forEach((op) => {
        source[op].forEach((df) => {
          if (
            !target[op].some(
              (of) =>
                of.analyticItem === df.analyticItem &&
                of.value === df.value &&
                of.subLabel === df.subLabel
            )
          ) {
            cnt++;
          }
        });
      });

      return cnt;
    };

    //selected filters displayed in right side of widget filter
    this.displayedSelectedFilters = this.initializeDisplayedFilters();
    this.originalTaxonomySelectedItems = Object.values(this.displayedSelectedFilters)
      .reduce((prev, curr) => prev.concat(curr), [])
      .filter((f) => f.analyticItem === 'taxonomy')
      .map((f) => f.value);

    if (countChange) {
      this.filterChangeCount = compareFilters(
        this.displayedSelectedFilters,
        this.originalSelectedFilters
      );
      this.filterChangeCount += compareFilters(
        this.originalSelectedFilters,
        this.displayedSelectedFilters
      );
    }
  }

  private async search(query, facetTypes: FacetType[] = [], options?: SearchOptions) {
    this.userQueryInvalidText = '';
    const keyword = this.modelSearch.toLowerCase();
    const searchOptions: SearchOptions = { ...options };

    if (keyword) {
      searchOptions.facetPrefix = `*${keyword}*`;
    }

    searchOptions.facetCustomType = this.widget.payload.analyticItem
      ? this.widget.payload.analyticItem
      : 'topics';
    if (this.widget.payload.selectedItems && this.widget.payload.selectedItems.length > 0) {
      searchOptions.facetCustomItems = this.widget.payload.selectedItems.toString();
    }
    searchOptions.facetTypes = facetTypes;
    searchOptions.isSocial = this.options.widgetDataSource.isSocial;
    searchOptions.timezone = this.timezoneService.timezone;

    const facetCount = facetTypes?.length ? undefined : 1;
    let facetsPromise: Promise<FacetList>;

    if (this.options.widgetDataSource.type === 'analysis') {
      facetsPromise = this.documentService.getFacetsWithinAnalysis(
        this.options.widgetDataSource.collectionName,
        this.options.widgetDataSource.analysisId,
        query,
        facetCount,
        undefined,
        searchOptions,
        this.options.projectId
      );
    } else {
      facetsPromise = this.documentService.getFacetsWithinGroup(
        this.options.projectId,
        this.options.widgetDataSource,
        query,
        facetCount,
        undefined,
        searchOptions
      );
    }

    return facetsPromise.then((res) => {
      return Object.fromEntries(
        Object.entries(res).filter(([_key, value]) => Object.keys(value).length)
      );
    });
  }

  private resetFilters() {
    this.modelSearch = '';
    this.filterChangeCount = 0;

    this.originalSelectedFilters = JSON.parse(JSON.stringify(this.displayedSelectedFilters));
    this.originalFilterSet = JSON.parse(JSON.stringify(this.filters));
    this.originalUserSearches = JSON.parse(JSON.stringify(this.displayedUserSearchFilters)) || {
      and: [],
      or: [],
      not: [],
    };
    this.originalTaxonomySelectedItems = Object.values(this.originalFilterSet.taxonomy)
      .reduce((prev, curr) => prev.concat(curr), [])
      .map((f) => f.value);
  }

  onSearchChange(clearSearch = false) {
    if (clearSearch) {
      this.modelSearch = '';
      this.refresh();
    }

    if (this.modelSearch.length > 2) {
      this.refresh();
    }
  }

  async refresh(): Promise<void> {
    this.isLoading.filters = true;

    //Creates string with previous filters to pass in to API
    this.query = this.initializeQuery();
    for (const tab of this.widgetFilterTabs) {
      if (tab.id === 'taxonomy') {
        tab.disabled = !this.widgetDataSource.hasTaxonomy;
        tab.disabledReason = translate('widgets.widget-filter.data-source-no-taxonomy');
      }
    }

    try {
      this.itemPages = {};
      this.facets = this.initializeFacets();
      if (this.modelSearch) {
        this.facets = await this.search(this.query);
      }
    } catch (err) {
      if (err.status !== 401) {
        this.logger.error(translate('widgets.widget-filter.facet-load-failed'));
      }
      this.dialogRef.close();
      return;
    }

    this.isLoading.filters = false;

    if (this.metadataItemKeys) {
      Object.values(this.metadataItemKeys).forEach((item) => (item.initialized = false));
    }

    if (this.analyticItemKeys) {
      Object.values(this.analyticItemKeys).forEach((item) => (item.initialized = false));
    }

    this.initializeAnalyticsTab();
    this.initializeMetadataTab();

    setTimeout(() => {
      if (this.currentAccordionItem.item) {
        let stateData = this.currentAccordionItem.item;
        if (this.currentAccordionItem.rollupLabel) {
          stateData += `:${this.currentAccordionItem.rollupLabel}`;
        }

        const accordionItem = this.host.nativeElement.querySelector(
          `[data-category="${stateData}"]`
        );
        if (accordionItem) {
          this.accordion.nativeElement.scrollTop = accordionItem.offsetTop;
        }

        if (!this.modelSearch) {
          this.setExpandedState(
            true,
            this.currentAccordionItem.item,
            this.currentAccordionItem.rollupLabel
          );
        }
      }
    });
  }

  private hasAnalyticResults(analyticItemKeyItem: AnalyticItemKeyItem) {
    if (this.isTextualSize(analyticItemKeyItem.item)) {
      return true;
    }

    if (analyticItemKeyItem.keys.length === 0) {
      return false;
    }

    if (!analyticItemKeyItem.isMultiLevel) {
      return true;
    }

    for (const rollupLabel of analyticItemKeyItem.subLabels.labels) {
      if (analyticItemKeyItem.subLabels.keys[rollupLabel].length > 0) {
        return true;
      }
    }

    return false;
  }

  public forWidgetGroup() {
    return this.options.forWidgetGroup;
  }

  private initializeAnalyticsTab() {
    this.analyticItems = this.initializeAnalyticItems();
    this.analyticItemKeys = this.initializeAnalyticItemKeys(
      this.analyticItems,
      this.analyticItemKeys
    );

    const itemsWithValues = Object.values(this.analyticItemKeys).filter((val) => val.keys.length);
    this.noAnalyticMatch = !this.analyticItems.length;
    if (
      itemsWithValues.length === 1 &&
      this.currentAccordionItem.item !== itemsWithValues[0].item
    ) {
      this.currentAccordionItem = {
        item: itemsWithValues[0].item,
        rollupLabel:
          itemsWithValues[0].isMultiLevel && itemsWithValues[0].subLabels.labels.length === 1
            ? itemsWithValues[0].subLabels.labels[0]
            : undefined,
      };
    }
  }

  private initializeMetadataTab() {
    this.metadataItems = this.initializeMetadataItems();
    this.metadataItemKeys = this.initializeAnalyticItemKeys(
      this.metadataItems,
      this.metadataItemKeys
    );
    this.noMetadataMatch = !this.metadataItems.length;
    this.noMetadataText = translate('widgets.widget-filter.no-documents-match-filters');
    if (
      this.noMetadataMatch &&
      this.widgetDataSource.hasDateData &&
      !this.choicesFor('createdAt')
    ) {
      this.noMetadataText = translate('widgets.widget-filter.no-more-filters-available');
    }

    if (!this.currentAccordionItem.item && this.metadataItems.length === 1) {
      this.currentAccordionItem = {
        item: this.metadataItems[0],
        rollupLabel: undefined,
      };
    }
  }

  private initializeAnalyticItems(): string[] {
    const items = Object.getOwnPropertyNames(this.facets);
    let result = [];

    for (let name of FilterService.analysisFields) {
      const config = this.filterService.getFilterConfigItem(name);
      if (name.endsWith('sentimentPolarity') && this.options.sentimentScale) {
        name = name + this.options.sentimentScale;
      }
      this.analyticItemDisplayMap[name] = this.translateTitleFor(name);
      this.setConfigTypeFor(name);
      if (config && config.choices && !this.modelSearch) {
        result.push(name);
      } else {
        if (items.indexOf(name) >= 0) {
          if (!config || !config.whenAnalyticItem) {
            result.push(name);
          }
        }
      }
    }

    if (this.widgetDataSource.isSocial) {
      result = result.filter((value) => value !== 'analyses.phrases.title');
    }

    if (this.widget.payload.type === 'powerScore') {
      result = result.filter((value) => value === 'ML-analyses.entities.title');
    }

    return result;
  }

  private initializeAnalyticItemKeys(
    analyticItems: string[],
    currentItemKeys: AnalyticItemTreeModel
  ): AnalyticItemTreeModel {
    const result = {} as AnalyticItemTreeModel;

    for (const item of analyticItems) {
      if (!this.isMultiLevel(item)) {
        if (this.isCreatedAtAnalyticItem(item) && !this.choicesFor(item)) {
          continue;
        }

        const keys = this.keysFor(item);
        result[item] = {
          subLabels: {
            labels: ['All'],
            displayLabels: [translate('analytic-items.all')],
          },
          isMultiLevel: false,
          keys,
          item,
        };
        for (const key of keys) {
          this.analyticItemKeyDisplayMap[item + key] = this.translateKeyFor(item, key);
          this.analyticItemKeyCountMap[item + key] = this.countFor(item, key, undefined);
        }
      } else {
        const subLabels = Object.getOwnPropertyNames(this.facets[item]);
        const keys = [];
        const subKeys = {};
        for (const subLabel of subLabels) {
          keys.push(...Object.getOwnPropertyNames(this.facets[item][subLabel].buckets));
          subKeys[subLabel] = this.keysFor(item, subLabel);
          for (const subKey of subKeys[subLabel]) {
            this.analyticItemKeyDisplayMap[item + subKey] = this.translateKeyFor(item, subKey);
            this.analyticItemKeyCountMap[item + subKey + subLabel] = this.countFor(
              item,
              subKey,
              subLabel
            );
          }
        }

        let displayLabels = [...subLabels];
        if (item.includes('topics')) {
          const translationKey = `analytic-items.${item.replace(/\./g, '_')}-subtypes`;
          const sublabelTranslations = translateObject(translationKey);
          if (sublabelTranslations !== translationKey) {
            displayLabels = subLabels.map((l) => sublabelTranslations[l.toLocaleLowerCase()]);
          }
        }

        result[item] = {
          subLabels: {
            labels: subLabels,
            displayLabels,
            keys: subKeys,
          },
          keys,
          isMultiLevel: true,
          item,
        };
      }

      result[item].choicesFor = this.choicesFor(item);
      result[item].hasChoicesFor = !!result[item].choicesFor;
      result[item].hasResults = this.hasAnalyticResults(result[item]);

      if (currentItemKeys) {
        result[item].initialized = currentItemKeys[item]?.initialized;
      }
    }

    return result;
  }

  private initializeMetadataItems(): string[] {
    const items = Object.getOwnPropertyNames(this.facets);

    let metaDataItems = items
      .filter((i) => i.startsWith('metadata'))
      .filter((i) => {
        const field = this.customFields.find((f) => f.fieldName === i);
        if (!field) {
          return false;
        }
        return (
          field.isDisplayed &&
          (field.isFilterable || this.temporarilyFilterableCustomFields.includes(i))
        );
      });

    metaDataItems = metaDataItems.sort((a, b) => {
      const nameA = this.customFields.find((f) => f.fieldName === a).description;
      const nameB = this.customFields.find((f) => f.fieldName === b).description;
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'accent' });
    });

    const customFieldsForFiltering = this.customFields.filter(
      (f) => f.isDisplayed && items.includes(f.fieldName)
    );

    this.nonFilterableCustomFields = customFieldsForFiltering.filter(
      (f) => !f.isFilterable && !this.temporarilyFilterableCustomFields.includes(f.fieldName)
    );
    this.filteredUnfilterableCustomFields = this.nonFilterableCustomFields;

    this.filterableCustomFields = customFieldsForFiltering
      .filter((f) => f.isFilterable)
      .concat(
        this.customFields.filter((f) =>
          this.temporarilyFilterableCustomFields.includes(f.fieldName)
        )
      );
    this.filteredFilterableCustomFields = this.filterableCustomFields;

    metaDataItems.unshift(...items.filter((i) => this.standardFields.includes(i)));

    if (this.widgetDataSource.hasDateData && this.choicesFor('createdAt')) {
      metaDataItems.unshift('createdAt');

      this.toggleSelectedDateChoice('createdAt', '7d');
    }

    metaDataItems.unshift(TEXTUAL_SIZE);

    for (const meta of metaDataItems) {
      this.analyticItemDisplayMap[meta] = this.translateTitleFor(meta);
      this.setConfigTypeFor(meta);
    }

    this.configTypeMap['floatValue'] = 'numeric';

    return metaDataItems;
  }

  private initializeSocialCustomFields() {
    this.customFields.push(
      ...FieldService.SOCIAL_FIELDS.filter((field) => !this.customFields.includes(field))
    );
  }

  public cancel(): void {
    this.closePopover(true);
    this.dialogRef.close();
  }

  get hasSelectedFilters(): boolean {
    const dsf = this.displayedSelectedFilters;
    const usf = this.displayedUserSearchFilters;
    return (
      dsf.and.length > 0 ||
      dsf.or.length > 0 ||
      dsf.not.length > 0 ||
      usf.and.length > 0 ||
      usf.or.length > 0 ||
      usf.not.length > 0
    );
  }

  public clearAllFilters(): void {
    this.dialogService
      .showWarningPopup({
        title: translate('widgets.widget-filter.clear-all-filters-prompt'),
        content: translate('widgets.widget-filter.change-not-permanent'),
        confirmTitle: translate('widgets.widget-filter.clear'),
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          for (const filterSetKey of Object.keys(this.filters)) {
            this.filters[filterSetKey] = { and: [], or: [], not: [] };
          }

          this.displayedUserSearchFilters = { and: [], or: [], not: [] };
          this.displayedSelectedFilters = { and: [], or: [], not: [] };
          this.sectionFilters = [];
          this.selected = [];
          this.selectedMap = {};

          this.resetFilters();
          this.refresh();
        }
      });
  }

  public confirm(multi = false): void {
    if (this.selected.length > 0) {
      this.dialogService
        .showWarningPopup({
          title: translate('widgets.widget-filter.warning'),
          content: translate('widgets.widget-filter.available-items-checked'),
          confirmTitle: translate('widgets.widget-filter.ok'),
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this.dialogRef.close({
              multi,
              filters: this.filters,
              userSearch: { userSearch: this.displayedUserSearchFilters },
              sectionFilter: this.selectedSections,
            });
          }
        });
      return;
    }

    this.dialogRef.close({
      multi,
      filters: this.filters,
      userSearch: { userSearch: this.displayedUserSearchFilters },
      sectionFilter: this.selectedSections,
    });
    if (!this.options.forWidgetGroup) {
      this.saveUserSearches();
    }
  }

  private getPrevUserSearches() {
    this.localStorageSavedSearchKey = `${LocalStorageKey.LOCAL_USER_DOCUMENT_SEARCHES}_${this.userId}_${this.options.dashboard.id}`;
    this.sessionStorageSavedSearchKey = `${LocalStorageKey.SESSION_USER_DOCUMENT_SEARCHES}_${this.options.dashboard.id}`;

    this.localStorageSearches =
      this.localStorageService.getItem(this.localStorageSavedSearchKey) || [];
    this.sessionStorageSearches =
      this.localStorageService.getItem(this.sessionStorageSavedSearchKey, 'session') || [];

    //combine local and session storage searches for text list
    this.userSavedSearches = Array.from(
      new Set([...this.localStorageSearches, ...this.sessionStorageSearches])
    );
  }

  onSearchTextItemRemoved(text: string) {
    this.localStorageSearches = this.localStorageSearches.filter((i) => i !== text);
    this.sessionStorageSearches = this.sessionStorageSearches.filter((i) => i !== text);
  }

  private saveUserSearches() {
    //selected user search filters to add to localstorage
    const newUserSearches = [];
    for (const operator of ['and', 'or', 'not']) {
      for (const search of this.displayedUserSearchFilters[operator]) {
        newUserSearches.push(search.value);
      }
    }

    //combine previous local storage applied searches with new ones
    const allSearchFilters = Array.from(
      new Set([...this.localStorageSearches, ...newUserSearches])
    );

    this.localStorageService.setItem(this.localStorageSavedSearchKey, allSearchFilters);
    this.localStorageService.setItem(
      this.sessionStorageSavedSearchKey,
      this.sessionStorageSearches,
      'session'
    );
  }

  public setHeaderText() {
    if (this.forWidgetGroup()) {
      this.headerText = translate('widgets.widget-filter.select-filters-for-group');
    } else {
      this.headerText = translate('widgets.widget-filter.select-filters-for-widget');
    }
  }

  public onEqualsExistsRadioChange(analyticItem: string) {
    if (this.modelEqualsExistsRadio !== '_equals_') {
      Object.keys(this.selectedMap).forEach((k) => {
        this.selectedMap[k] = false;
      });

      this.selected = [{ analyticItem, value: this.modelEqualsExistsRadio }];
    } else {
      const existsIndex = this.selected.findIndex((s) => this.isExistsValue(s.value));
      if (existsIndex >= 0) {
        this.selected.splice(existsIndex, 1);
      }
    }

    this.setIsOperatorAllowed();
  }

  public isExistsValue(value: string) {
    return value === '_exists_' || value === '_notExists_';
  }

  private getFacetTypeByAnalyticItem(analyticItem: string): FacetType {
    if (!analyticItem) {
      return null;
    }

    if (analyticItem.includes('sentiment')) {
      return 'sentiment';
    }

    if (this.isMetadataAnalyticItem(analyticItem)) {
      return 'metadata';
    }

    if (analyticItem.includes('createdAt')) {
      return 'dates';
    }

    if (analyticItem.startsWith('analyses.') || analyticItem.startsWith('ML-analyses.')) {
      return analyticItem.split('.')[1] as FacetType;
    }

    if (analyticItem.includes('social')) {
      return 'social';
    }

    return analyticItem as FacetType;
  }

  private isMetadataAnalyticItem(analyticItem: string): boolean {
    return analyticItem.startsWith('metadata');
  }

  private isCreatedAtAnalyticItem(analyticItem: string): boolean {
    return analyticItem === 'createdAt';
  }

  private isStandardField(analyticItem: string): boolean {
    return this.standardFields.includes(analyticItem);
  }

  public openFilterMetadataAddPopover(popover: NgbPopover, $event?) {
    if (this.filteredUnfilterableCustomFields.length < 1) {
      return;
    }
    this.addfieldPopoverMode = true;
    this.metaTemporaryChecked = false;
    this.openMetadataPopover(popover, $event);
  }

  public openFilterMetadataRemovePopover(popover: NgbPopover, $event?) {
    if (this.filteredFilterableCustomFields.length < 1) {
      return;
    }
    this.addfieldPopoverMode = false;
    this.openMetadataPopover(popover, $event);
  }

  public openMetadataPopover(popover: NgbPopover, $event) {
    this.popOverSelectedMetadata = [];
    if (this.activePopover) {
      this.activePopover.close();
    }
    popover.open({ $implicit: popover });
    if ($event) {
      $event.stopImmediatePropagation();
    }
    this.activePopover = popover;
  }

  public onPopoverFieldSelected(field: CustomField) {
    const find = this.popOverSelectedMetadata.find((f) => f.id === field.id);
    if (find) {
      this.popOverSelectedMetadata = this.popOverSelectedMetadata.filter((f) => f.id !== field.id);
    } else {
      this.popOverSelectedMetadata.push(field);
    }
  }

  public async addFilterMetadata() {
    try {
      this.isLoading.filters = true;
      this.closePopover(false);
      const fieldNames = this.popOverSelectedMetadata.map((f) => f.fieldName);
      if (this.metaTemporaryChecked) {
        this.temporarilyFilterableCustomFields = this.temporarilyFilterableCustomFields.concat(
          [].concat(fieldNames)
        );
      } else {
        for (const fieldName of fieldNames) {
          await this.updateField(fieldName, true);
        }
      }
      await this.refreshMetadataAfterPopover();
      await this.accountService.groupFieldsSub.load(true);
      this.alertService.showToast(translate('widgets.widget-filter.filterable-add-alert'));
    } finally {
      this.isLoading.filters = false;
    }
  }

  public async removeFilterMetadata() {
    try {
      this.isLoading.filters = true;
      this.closePopover(false);
      const fieldNames = this.popOverSelectedMetadata.map((f) => f.fieldName);
      for (const fieldName of fieldNames) {
        if (this.temporarilyFilterableCustomFields.includes(fieldName)) {
          this.temporarilyFilterableCustomFields.forEach((item, index) => {
            if (item === fieldName) {
              this.temporarilyFilterableCustomFields.splice(index, 1);
            }
          });
        } else {
          await this.updateField(fieldName, false);
        }
      }
      await this.refreshMetadataAfterPopover();
      await this.accountService.groupFieldsSub.load(true);
      this.alertService.showToast(translate('widgets.widget-filter.filterable-remove-alert'));
    } finally {
      this.isLoading.filters = false;
    }
  }

  private async refreshMetadataAfterPopover() {
    const accountMetadata = await this.accountService.getFieldsForCurrentGroup();
    this.customFields = accountMetadata.metadata.map((f) => ({
      id: f.id,
      label: f.label,
      description: f.description,
      fieldName: `metadata.${f.fieldName}`,
      fieldType: f.fieldType,
      isFilterable: f.metadata?.filterable ?? true,
      aliasedFields: f.aliasedFields,
      isDisplayed: f.metadata?.display ?? true,
    }));
    if (this.options.widgetDataSource.isSocial) {
      this.initializeSocialCustomFields();
    }
    this.initializeMetadataTab();
  }

  public closePopover(clearSelected: boolean) {
    if (this.activePopover) {
      this.filteredUnfilterableCustomFields = this.nonFilterableCustomFields;
      this.filteredFilterableCustomFields = this.filterableCustomFields;
      this.activePopover.close();
      if (clearSelected) {
        this.popOverSelectedMetadata = [];
      }
    }
  }

  public internalPropsAdd = {
    searchBox: {
      placeholder: translate('widgets.widget-filter.search'),
      onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
        this.ngZone.run(() => {
          this.filteredUnfilterableCustomFields = this.nonFilterableCustomFields.filter(
            (s) =>
              s.description.toLocaleLowerCase().indexOf($event.searchText?.toLocaleLowerCase()) >= 0
          );
        });
      },
    },
  };

  public internalPropsRemove = {
    searchBox: {
      placeholder: translate('widgets.widget-filter.search'),
      onChangeOverride: ($event: SearchBox_onChangeOverrideEvent) => {
        this.ngZone.run(() => {
          this.filteredFilterableCustomFields = this.filterableCustomFields.filter(
            (s) =>
              s.description.toLocaleLowerCase().indexOf($event.searchText?.toLocaleLowerCase()) >= 0
          );
        });
      },
    },
  };

  public async updateField(fieldName: string, filterable: boolean): Promise<void> {
    const fieldFromCurrentFields = this.customFields.find((f) => f.fieldName === fieldName);
    if (fieldFromCurrentFields) {
      this.isLoading.filters = true;
      const currentField = await this.fieldService.getFieldById(fieldFromCurrentFields.id);

      if (currentField) {
        await this.fieldService.updateField(
          currentField.id,
          currentField.label,
          currentField.description,
          currentField.fieldType,
          {
            rating: currentField.metadata?.rating,
            weight: currentField.metadata?.weight,
            redacted: currentField.metadata?.redacted,
            redactionBehavior: currentField.metadata?.redactionBehavior,
            filterable,
            calculationType: currentField.metadata?.calculationType,
            calculationDefinition: currentField.metadata?.calculationDefinition,
          }
        );
      }
    }
  }

  get hasSections(): boolean {
    return this.options.widgetDataSource.hasSections;
  }

  get showSections(): boolean {
    return this.hasSections || !!this.filterSelectedSections;
  }

  private async loadSectionMetadata(): Promise<void> {
    if (!this.hasSections) {
      return;
    }

    try {
      const allMetadata = await this.sectionService.loadAllSectionMetadata(
        this.widget.payload.projectId,
        this.widgetDataSource
      );

      this.sectionMetadata.push(...allMetadata);
    } catch (error) {
      this.logger.error(translate('widgets.widget-filter.failed-to-load-sections'), { error });
    }
  }

  onToggleSectionFiltering(isToggled: boolean): void {
    this.isSectionFilteringActive = isToggled;

    if (!isToggled) {
      for (let index = this.sectionFilters.length - 1; index >= 0; index--) {
        const filter = this.sectionFilters[index];
        this.remove('and', filter);
      }
    } else {
      this.selectedSections = [];
    }
  }

  onSectionSelected(selectedSection: SelectedSection[]): void {
    this.selectedSections = selectedSection;
    this.sectionSelectionText = undefined;

    if (!SectionService.isSelectionComplete(this.selectedSections)) {
      this.sectionSelectionText = translate('widgets.analytic-item-selector.must-select-section');
    }

    this.updateSectionInfoOnFilters();
  }

  private updateSectionInfoOnFilters(): void {
    this.sectionFilters.forEach((f) => {
      f.selectedSections = this.selectedSections;
    });
  }

  private clearAndRemoveSectionFilter(filter: Filter): void {
    filter.sectionField = undefined;
    filter.sectionBuckets = undefined;
    filter.selectedSections = undefined;

    this.sectionFilters.splice(this.sectionFilters.indexOf(filter), 1);
  }

  private setAndAddSectionFilter(filter: Filter): void {
    filter.selectedSections = this.selectedSections;

    this.sectionFilters.push(filter);
  }

  private async initSectionFiltering(): Promise<void> {
    await this.loadSectionMetadata();

    this.loadSectionFilters();

    if (this.isSectionFilteringActive) {
      this.selectedSections = this.filterSelectedSections;
    }

    this.isSectionFilteringInitialized = true;
    this.sectionFilterTabs = [
      ...this.sectionFilterTabs,
      {
        title: translate('widgets.widget-filter.section-filters'),
        id: 'sections',
        disabled: false,
      },
    ];
  }

  private loadSectionFilters(): void {
    this.sectionFilters.push(...getSectionFilters(this.filters));
    this.sectionFilters.push(...getSectionFilters({ userSearch: this.displayedUserSearchFilters }));

    if (this.options.widgetGroup) {
      const wgSectionFilters = getSectionFilters(this.options.widgetGroup.filters);
      if (wgSectionFilters.length) {
        this.hasWgSectionFilter = true;

        this.sectionFilters.push(...wgSectionFilters);
      }
    }

    if (this.sectionFilters.length || this.widget.payload.sectionFilter) {
      this.isSectionFilteringActive = true;

      if (this.sectionFilters.length) {
        if (this.sectionFilters[0].selectedSections?.length) {
          this.filterSelectedSections = this.sectionFilters[0].selectedSections;
        } else {
          this.filterSelectedSections = [
            {
              field: this.sectionFilters[0].sectionField,
              buckets: this.sectionFilters[0].sectionBuckets,
            },
          ];
        }
      } else {
        this.filterSelectedSections = this.widget.payload.sectionFilter;
      }
    }
  }

  get cantModifySectionFilterDueToWg(): boolean {
    return (
      this.hasWgSectionFilter &&
      this.options.widgetGroup.id !== this.options.widget.id &&
      this.options.widget.id !== 'dummy'
    );
  }

  onSectionTabChange(_sectionTabId: string) {
    // TODO tbd if needed at all
  }
}
